/**
 * Pure role decisions for the store editor, keyed on the role SLUG.
 *
 * WHY SLUGS AND NOT IDS
 * ---------------------
 * Role ids are data, not constants. ``flask seed`` inserts the seven
 * platform roles into an empty table, so on every sandbox and every test
 * database they land at ids 1..7 and Shopper is 5. On PRODUCTION those
 * rows were created through a different path and are numbered 8..14 --
 * Shopper is 12, Client Admin 11, Owner 9.
 *
 * A comparison against a role-id literal is therefore correct on every
 * database it is tested against and wrong on the only one that matters.
 * That is not hypothetical: it took the AdventHealth storefront down on
 * 2026-09-07, and it silently broke the store editor's shopper save
 * (``role_id == 5`` never matched, so ``shopperAccount`` stayed null and
 * ``POST /campaign/updateShopper`` -- which writes the shopper username
 * and password -- never fired; #3579).
 *
 * ``roles.slug`` is NOT NULL and UNIQUE and survived the PR-3 role
 * renames intact, which is why the backend resolver
 * (``api/src/models/role_ids.py``) keys on it. These helpers are the
 * browser-side counterpart, extracted from StoreAddEdit.vue so the
 * decisions can be tested without mounting that (very large) component.
 *
 * The slug reaches the browser on ``account.role_slug``: exposed on the
 * Account model as a SAFRS ``jsonapi_attr`` and joined into the store
 * editor's own loader (``getCampaignById`` ->
 * ``getAccountsByCampaignId``), which is raw SQL and does not go through
 * the model.
 */

/** Platform role slugs. Mirrors ``api/src/models/role_ids.py``. */
export const ROLE_SLUGS = {
    SUPER_ADMIN: "super_admin",
    OWNER: "owner",
    SALES_EXECUTIVE: "sales_executive",
    CLIENT_ADMIN: "client_admin",
    SHOPPER: "shopper",
    BUILDER: "builder",
    STORE_MANAGER: "store_manager",
};

/**
 * How the store-editor save path should handle one of a store's accounts.
 *
 * Three outcomes, matching the three branches of the save loop:
 *
 *   - ``"shopper"``  the store's shopper. Its credentials are pushed to
 *                    ``POST /campaign/updateShopper``.
 *   - ``"skip"``     not created or updated from the store editor. Covers
 *                    anonymous storefront visitors (shopper-role rows that
 *                    are never the store login; naming one made
 *                    updateShopper move the real shopper's link onto it --
 *                    the loader filters them too), the legacy Client Admin row (the client contact is
 *                    now the first-class Client attached via
 *                    ``stores.client_id``, so a stray role row must not be
 *                    resurrected here) plus Super Admin and Sales
 *                    Executive, which are not store-scoped accounts.
 *   - ``"account"``  an ordinary account: create or update normally.
 *
 * An account with no recognisable slug falls through to ``"account"``
 * rather than ``"shopper"``. That direction matters: misclassifying an
 * unknown row as the shopper would push shopper credentials at the wrong
 * account, which is a credential leak rather than a cosmetic error.
 *
 * @param {{role_slug?: string, is_anonymous?: boolean}} account - an
 *   account from the store editor's campaign payload.
 * @returns {"shopper"|"skip"|"account"}
 */
export function classifyStoreAccount(account) {
    if (account && account.is_anonymous) {
        return "skip";
    }
    const slug = account && account.role_slug;

    if (slug === ROLE_SLUGS.SHOPPER) {
        return "shopper";
    }
    if (
        slug === ROLE_SLUGS.CLIENT_ADMIN ||
        slug === ROLE_SLUGS.SUPER_ADMIN ||
        slug === ROLE_SLUGS.SALES_EXECUTIVE
    ) {
        return "skip";
    }
    return "account";
}

/**
 * The store's legacy Client Admin account, if one is still attached.
 *
 * Used only to label the "client store" sidebar section with the buyer's
 * name. Returns undefined when there is none -- the caller falls back to
 * the store/company name.
 *
 * @param {Array<{role_slug?: string}>} accounts
 * @returns {object|undefined}
 */
export function findClientAdmin(accounts) {
    if (!Array.isArray(accounts)) {
        return undefined;
    }
    return accounts.find((a) => a && a.role_slug === ROLE_SLUGS.CLIENT_ADMIN);
}

/**
 * Presentation ``kind`` for the Stores-list Owner avatar.
 *
 * Mirrors the backend's own owner/builder decision in
 * ``api/src/endpoints/distributor.py`` (``display_role = "owner" if
 * row.role_slug == "owner" else "builder"``). Everything that is not an
 * Owner -- including Super Admin, which the old ``role_id === 1``
 * literal actually selected on a seeded database -- styles as a builder.
 *
 * @param {string|null|undefined} creatorRoleSlug
 * @returns {"owner"|"builder"}
 */
export function avatarKindForRoleSlug(creatorRoleSlug) {
    return creatorRoleSlug === ROLE_SLUGS.OWNER ? "owner" : "builder";
}
